import { useState, useEffect } from 'react';

// Material UI
import { Grid, Icon, Chip } from '@mui/material';

// Componentes
import AutoComplete from '@components/Selects/Autocomplete';
import AdvancedSelect from '@components/Selects/AdvancedSelect';
import TextFieldTK from '@components/Form/TextFieldTK';

// Utils
import { isNullOrUndefined } from '@utils/validations';
import { getFormikUtil } from '@utils/Utilities';
import { Swal } from '@utils/alerts';

// Servicios
import CatalogService from '@services/CatalogServices';

/* Afecta a:
      TipoVialidad
      CalleVive
      NumExtVive
      NumIntVive
      idEntidadVive 
      idMunicipioVive
      idLocalidadVive
      ColoniaVive
      CPVive

      isBasic = true solo pide:
        idEntidadVive 
        idMunicipioVive
*/

const AddressForm = (props) => {
  const { catalogs, isLoading, formik, isBasic = false, showChip = true } = props;

  const [catMunicipios, setMunicipios] = useState([]);
  const [, setCatLocalidades] = useState([]);
  const [catColonias, setCatColonias] = useState([]);

  const [isLoadingCat, setIsLoadingCat] = useState({
    municipios: false,
    localidades: false,
    colonias: false,
  });

  const getFormik = (value) => getFormikUtil(formik, 'address', value);

  const changeFormik = (name, value = '') =>
    formik.handleChange({ target: { name: 'address.' + name, value } });

  const getLocalidadUnoValue = (idEntidad, idMunicipio) => {
    const entidad = parseInt(idEntidad) || 0;
    const municipio = parseInt(idMunicipio) || 0;

    if (!entidad || !municipio) return '';

    return parseInt(`${`${entidad}`.padStart(2, '0')}${`${municipio}`.padStart(3, '0')}0001`);
  };

  const getCatalogLocalidadUnoValue = (localidades = []) => {
    const isLocalidadUno = (item = {}) => {
      const values = [
        item.value,
        item.idLocalidad,
        item.IdLocalidad,
        item.Localidad,
        item.localidad,
        item.claveLocalidad,
        item.ClaveLocalidad,
        item.clave,
        item.Clave,
      ];

      return values.some((localidad) => {
        const value = `${localidad ?? ''}`.trim();

        if (!value) return false;

        return parseInt(value) === 1 || value.padStart(4, '0').endsWith('0001');
      });
    };

    const localidadUno = localidades.find(isLocalidadUno) || localidades[0];

    return localidadUno?.value ?? '';
  };

  useEffect(() => {
    const value = parseInt(getFormik('idEntidadVive')?.value) || 0;
    if (value) handleChangeEntidad({ value });
    // eslint-disable-next-line
  }, []);

  const handleChangeEntidad = async ({ value }) => {
    changeFormik('idEntidadVive', value);
    changeFormik('idMunicipioVive', '');

    if (!isBasic) {
      changeFormik('idLocalidadVive', '');
      changeFormik('ColoniaVive', '');
      changeFormik('CPVive', '');
    }

    if (isBasic) return;

    try {
      setIsLoadingCat((prev) => ({ ...prev, municipios: true }));

      const params = [{ id: 'municipios_nacional', filtered: [{ id: 'idEntidad', filter: '=', value }] }];

      const result = await CatalogService.getCatalogsPublicEvents({ catalogs: params });
      const { results, response, message } = result;

      if (results) {
        setMunicipios(response.catalogs.municipios_nacional);
        setCatLocalidades([]);
        setCatColonias([]);
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoadingCat((prev) => ({ ...prev, municipios: false }));
    }
  };

  const handleChangeMunicipio = async ({ value }) => {
    const idEntidad = parseInt(getFormik('idEntidadVive')?.value) || 0;
    const localidadUno = getLocalidadUnoValue(idEntidad, value);

    changeFormik('idMunicipioVive', value);

    if (!isBasic) {
      changeFormik('idLocalidadVive', localidadUno);
      changeFormik('ColoniaVive', '');
      changeFormik('CPVive', '');
    }

    if (isBasic) return;

    try {
      setIsLoadingCat((prev) => ({ ...prev, localidades: true, colonias: true }));

      const params = [
        {
          id: 'localidades_nacional',
          filtered: [
            { id: 'idEntidad', filter: '=', value: idEntidad },
            { id: 'idMunicipio', filter: '=', value },
          ],
        },
      ];

      if (idEntidad === 11) {
        params.push({ id: 'colonias', filtered: [{ id: 'idMunicipio', filter: '=', value }] });
      }

      const result = await CatalogService.getCatalogsPublicEvents({ catalogs: params });
      const { results, response, message } = result;

      if (results) {
        const localidades = response.catalogs.localidades_nacional || [];

        setCatLocalidades(localidades);
        setCatColonias(response.catalogs?.colonias || []);

        const findFirst = getCatalogLocalidadUnoValue(localidades);

        if (findFirst) changeFormik('idLocalidadVive', findFirst);
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoadingCat((prev) => ({ ...prev, localidades: false, colonias: false }));
    }
  };

  const handleChangeColonia = ({ CP, label }) => {
    const localCP = label?.length ? CP || getFormik('CPVive')?.value : '';

    const validCP = (value) => (isNullOrUndefined(value) || isNaN(parseInt(value)) ? '' : parseInt(value));

    changeFormik('ColoniaVive', label);
    changeFormik('CPVive', validCP(localCP));
  };

  return (
    <Grid container spacing={3}>
      {showChip && (
        <Grid item xs={12}>
          <Chip icon={<Icon>school</Icon>} label="Domicilio donde vive" />
        </Grid>
      )}

      {!isBasic && (
        <>
          {/* <Grid item xs={12} sm={6} md={3} lg={3}>
            <AdvancedSelect
              required
              name="address.TipoVialidad"
              label="Tipo de Vialidad *"
              placeholder="Selecciona tu vialidad"
              value={getFormik('TipoVialidad')?.value}
              options={catalogs.tipo_vialidad}
              isLoading={isLoading}
              error={getFormik('TipoVialidad')?.error}
              helperText={getFormik('TipoVialidad')?.helperText}
              onChange={formik.handleChange}
              useTarget
            />
          </Grid> */}

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextFieldTK
              required
              fullWidth
              label="Calle"
              name="address.CalleVive"
              value={getFormik('CalleVive')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('CalleVive')?.error}
              helperText={getFormik('CalleVive')?.helperText}
              icon={{ text: getFormik('TipoVialidad')?.value || '' }}
              useTrim
              toUpperCase
            />
          </Grid>

          <Grid item xs={6} sm={6} md={3} lg={3}>
            <TextFieldTK
              required
              fullWidth
              label="Número Ext."
              name="address.NumExtVive"
              value={getFormik('NumExtVive')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('NumExtVive')?.error}
              helperText={getFormik('NumExtVive')?.helperText}
              toUpperCase
              useTrim
            />
          </Grid>

          <Grid item xs={6} sm={6} md={3} lg={3}>
            <TextFieldTK
              fullWidth
              label="Número Int."
              name="address.NumIntVive"
              value={getFormik('NumIntVive')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('NumIntVive')?.error}
              helperText={getFormik('NumIntVive')?.helperText}
              toUpperCase
              useTrim
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <AdvancedSelect
          required
          name="address.idEntidadVive"
          label="Estado *"
          placeholder="Selecciona tu estado"
          value={getFormik('idEntidadVive')?.value}
          options={catalogs?.entidades?.filter((item) => item.value <= 50) || []}
          isLoading={isLoading}
          error={getFormik('idEntidadVive')?.error}
          helperText={getFormik('idEntidadVive')?.helperText}
          onChange={handleChangeEntidad}
          isSearchable
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <AdvancedSelect
          required
          name="address.idMunicipioVive"
          label="Municipio *"
          placeholder="Selecciona tu municipio"
          value={getFormik('idMunicipioVive')?.value}
          options={catMunicipios}
          isLoading={isLoading || isLoadingCat.municipios}
          error={getFormik('idMunicipioVive')?.error}
          helperText={
            getFormik('idEntidadVive')?.value && catMunicipios?.length === 0
              ? 'Sin opciones. Selecciona otro estado y vuelva a seleccionar el estado buscado para recargar los municipios.'
              : getFormik('idMunicipioVive')?.helperText
          }
          onChange={handleChangeMunicipio}
          isSearchable
        />
      </Grid>

      {!isBasic && (
        <>
          {/* <Grid item xs={12} sm={6} md={3} lg={3}>
            <AdvancedSelect
              required
              name="address.idLocalidadVive"
              label="Localidad *"
              placeholder="Selecciona tu localidad"
              value={getFormik('idLocalidadVive')?.value}
              options={catLocalidades}
              isLoading={isLoading || isLoadingCat.localidades}
              error={getFormik('idLocalidadVive')?.error}
              helperText={getFormik('idLocalidadVive')?.helperText}
              onChange={formik.handleChange}
              isSearchable
              useTarget
            />
          </Grid> */}

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <AutoComplete
              required
              size="small"
              id="ColoniaVive"
              name="address.ColoniaVive"
              label="Colonia *"
              value={('' + getFormik('ColoniaVive')?.value)?.toUpperCase()}
              setSelect={handleChangeColonia}
              data={catColonias}
              error={getFormik('ColoniaVive')?.error}
              helperText={getFormik('ColoniaVive')?.helperText}
              loading={isLoading || isLoadingCat.colonias}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextFieldTK
              required
              fullWidth
              label="Código Postal"
              name="address.CPVive"
              value={getFormik('CPVive')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('CPVive')?.error}
              helperText={getFormik('CPVive')?.helperText}
              isNumber
              maxLength={5}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default AddressForm;
